<script setup lang="ts">
import usePoolStore from '~/store/pool'
import { UserRank } from '~/interfaces/user'
import type { PoolStation, PoolStationPaymentMean } from '~/interfaces/station'
import useUserStore from '~/store/user'
import EditConnector from './EditConnector.vue'
import EditStation from './EditStation.vue'
import IconForward from '../Icons/IconForward.vue'
import { PoolRealtimeState, type PoolStationConnector } from '#cmui/types'

defineProps({
    isContainedInDrawer: {
        type: Boolean,
        default: true
    }
})

const { t, locale } = useI18n()
const userRankStates = UserRank
const { LEGACY_URL } = useRuntimeConfig().public

const openBadgeSection = ref([] as boolean[])

const defaultPassImg = ref(
    `${LEGACY_URL}/medias/v2/emsp_payment_means/file/86x56/default/card-default.png`
)

const defaultConnectorImg = ref(
    `${LEGACY_URL}/medias/v2/connector_types/icon/original/unknown/plug.svg`
)

const getConnectorImgUrl = (connector: PoolStationConnector): string =>
    connector.connectorType
        ? `${LEGACY_URL}/medias/v2/connector_types/icon/original/${connector.connectorType?.id}/${connector.connectorType?.icon}`
        : defaultConnectorImg.value

const getRealtimeState = (connector: PoolStationConnector): string => {
    switch (connector.realtimeStatus) {
        case PoolRealtimeState.AVAILABLE:
            return t('plug.state.available')
        case PoolRealtimeState.UNAVAILABLE:
            return t('plug.state.occupied')
        case PoolRealtimeState.OUT_OF_ORDER:
        case PoolRealtimeState.FAULTED:
            return t('plug.state.out_of_service')
        default:
            return t('plug.state.unknown')
    }
}

const getClassByState = (connector: PoolStationConnector): string => {
    switch (connector.realtimeStatus) {
        case PoolRealtimeState.AVAILABLE:
            return 'bg-green-l-200 text-green-l-700'
        case PoolRealtimeState.UNAVAILABLE:
            return 'bg-red-l-100 text-red-600'
        default:
            return 'bg-gray-l-200 text-gray-l-700'
    }
}

const getPassImgUrl = (pass: PoolStationPaymentMean | undefined): string => {
    if (pass?.icon && pass?.id > 3) {
        return `${LEGACY_URL}/medias/v2/emsp_payment_means/file/172x112/${pass.id - 3}/${pass.icon}`
    }

    return defaultPassImg.value
}

const openOrCloseBadgesSection = (index: number) => {
    openBadgeSection.value[index] = !openBadgeSection.value[index]
}

const chargemapPassPayment = (
    station: PoolStation
): PoolStationPaymentMean | undefined =>
    (station?.paymentMeans || []).find(
        (payment) => payment.name === 'Chargemap'
    )

const otherPaymentsThanChargemapPass = (
    station: PoolStation
): PoolStationPaymentMean[] | [] =>
    (station?.paymentMeans || []).filter(
        (payment) => payment.name !== 'Chargemap'
    )

const redirectToChargemap = (path: string) => {
    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            { action: 'openUrl', url: path },
            document.referrer
        )
    } else {
        const newWnd = window.open(
            `https://${locale.value}.chargemap.com/${path}`,
            '_top'
        )

        if (newWnd) {
            newWnd.opener = null
        }
    }
}

onMounted(() => {
    usePoolStore().fetchPoolStationsFromCommunity()
})
</script>

<template>
    <div class="w-full">
        <Loader v-if="usePoolStore().communityPoolStationsLoader" />
        <div v-else>
            <div
                v-for="(station, index) in usePoolStore().poolStations"
                :key="`pool-station-${station.id}`"
            >
                <div :class="[!isContainedInDrawer && 'px-4 py-2']">
                    <h2 class="font-semibold text-xl text-gray-l-800 text-left">
                        {{
                            station.label
                                ? station.label
                                : `${$t('station.singular')} #${index + 1}`
                        }}
                    </h2>
                </div>
                <EditStation
                    v-if="useUserStore().userRank === userRankStates.ADMIN"
                    :station-id="station.id"
                    :station
                />
                <div
                    :class="[
                        !isContainedInDrawer && 'bg-white rounded-lg px-5 py-2'
                    ]"
                >
                    <div
                        v-if="station.authenticationMethods?.length"
                        class="text-gray-station text-sm flex items-center my-1.5"
                    >
                        <span class="w-1/2">
                            {{ $t('authentication_system') }}
                        </span>
                        <div class="flex gap-3">
                            <span
                                v-for="method in station.authenticationMethods"
                                :key="`pool-station-${station.id}-method-${method.id}`"
                                class="bg-gray-station text-white py-1 px-2 rounded-full text-xs"
                            >
                                {{ method.label }}
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="chargemapPassPayment(station)"
                        class="text-gray-station text-sm flex my-1.5"
                    >
                        <p class="w-1/2">{{ $t('required_pass') }}</p>
                        <div
                            class="flex items-center gap-2 text-gray-l-700 cursor-pointer"
                            @click="redirectToChargemap('pass')"
                        >
                            <NuxtImg
                                alt="Chargemap"
                                loading="lazy"
                                class="w-8 h-5"
                                :src="
                                    getPassImgUrl(chargemapPassPayment(station))
                                "
                                :placeholder="defaultPassImg"
                            />
                            Chargemap
                        </div>
                    </div>

                    <div
                        v-if="otherPaymentsThanChargemapPass(station)?.length"
                        class="text-button-primary-branding-hover text-sm flex my-1.5 flex-col"
                    >
                        <div
                            class="flex items-center gap-2 cursor-pointer w-fit"
                            @click="openOrCloseBadgesSection(index)"
                        >
                            <IconForward
                                class="transition-transform duration-200 h-3.5"
                                :class="[
                                    openBadgeSection[index] &&
                                        'transform rotate-90'
                                ]"
                            />
                            <span>{{ $t('compatible_passes') }}</span>
                        </div>
                        <div
                            class="transition-max-h duration-200"
                            :class="[
                                openBadgeSection[index]
                                    ? 'max-h-448 h-auto'
                                    : 'max-h-0 overflow-hidden'
                            ]"
                        >
                            <div
                                class="m-2 flex flex-wrap gap-1 text-gray-l-700 w-full"
                            >
                                <div
                                    v-for="payment of otherPaymentsThanChargemapPass(
                                        station
                                    )"
                                    :key="`pool-station-${station.id}-payment-${payment.id}`"
                                    class="flex gap-1 w-2/5"
                                >
                                    <NuxtImg
                                        loading="lazy"
                                        class="w-8 h-5"
                                        :src="getPassImgUrl(payment)"
                                        :alt="payment.name"
                                        :placeholder="defaultPassImg"
                                    />
                                    <span>{{ payment.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr
                        v-if="
                            !isContainedInDrawer &&
                            (station.authenticationMethods?.length ||
                                otherPaymentsThanChargemapPass(station)?.length)
                        "
                        class="border-t-1 my-2"
                    />
                    <div
                        v-for="evse in station.evses"
                        :key="`pool-station-${station.id}-evse-${evse.id}`"
                    >
                        <div
                            v-for="connector in evse.connectors"
                            :key="`pool-station-${station.id}-evse-${evse.id}-connector-${connector.id}`"
                        >
                            <EditConnector
                                v-if="
                                    useUserStore().userRank ===
                                    userRankStates.ADMIN
                                "
                                :connector
                                :evse
                                :station-id="station.id"
                            />
                            <div class="flex items-center gap-3 my-3">
                                <NuxtImg
                                    class="p-2.5 rounded-lg border-1 border-gray-l-300"
                                    loading="lazy"
                                    :src="getConnectorImgUrl(connector)"
                                    :placeholder="defaultConnectorImg"
                                    :alt="
                                        connector.connectorType?.label ||
                                        t('word.connector')
                                    "
                                />
                                <div class="flex flex-col items-start">
                                    <h3
                                        v-if="connector.connectorType?.label"
                                        class="text-gray-l-800 text-lg"
                                    >
                                        {{
                                            connector.connectorType.label.toUpperCase()
                                        }}
                                    </h3>
                                    <p class="text-gray-l-500 text-sm">
                                        <span>
                                            {{ connector.speed.name }} /
                                            {{ connector.power }}kW
                                        </span>
                                        <span
                                            v-if="connector.currentType?.name"
                                        >
                                            / {{ connector.currentType.name }}
                                        </span>
                                    </p>
                                    <span
                                        class="px-2.5 py-1.5 rounded-3xl text-xs mt-1"
                                        :class="getClassByState(connector)"
                                    >
                                        {{ getRealtimeState(connector) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
